<script lang="ts" setup="">
import { flip, offset, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import emojiDataUrl from 'emoji-picker-element-data/en/emojibase/data.json?url';
import { onMounted, onUnmounted, ref } from 'vue';

import { GrammarOverlayCard } from '@nora-monorepo/grammar-ui';

import type { EmojiPickedEvent } from '#ui-shared/components/emoji-picker/types';
import { useDarkModeStore } from '#ui-shared/stores/darkmode-store';
import { getTailwindCSSVariable } from '#ui-shared/utils/get-css-variable';
import 'emoji-picker-element';

const props = defineProps<{
  toggleRef: HTMLElement;
}>();

const emit = defineEmits<EmojiPickerEmit>();
const pickerRef = ref();
const toggleRef = ref<HTMLElement>(props.toggleRef);
const overlayCard = ref();
const darkModeStore = useDarkModeStore();

interface EmojiPickerEmit {
  (event: 'emojiClicked', emoji: string): void;
  (event: 'close'): void;
}

function emojiClickListener(event: CustomEvent<EmojiPickedEvent>) {
  emit('emojiClicked', event.detail.unicode);
}

onClickOutside(
  overlayCard,
  () => {
    emit('close');
  },
  {
    ignore: [props.toggleRef],
  },
);

const { floatingStyles } = useFloating(toggleRef, pickerRef, {
  placement: 'top',
  middleware: [
    offset({
      crossAxis: 100,
    }),
    flip(),
  ],
  transform: false,
});

onMounted(() => {
  if (pickerRef.value) {
    pickerRef.value.dataSource = emojiDataUrl;
    pickerRef.value.addEventListener('emoji-click', emojiClickListener);

    const style = document.createElement('style');
    const backgroundColor = darkModeStore.isDark
      ? getTailwindCSSVariable('dark-bg-secondary')
      : getTailwindCSSVariable('light-bg-secondary');

    style.textContent = `
    .picker {
      border-radius: 0.5rem;
    }

    .favorites {
      display: none !important;
    }

    input.search {
      font-family: 'Source Sans Pro', 'sans-serif';
      font-size: 0.875rem;
      background-color: ${backgroundColor};
    }`;

    pickerRef.value.shadowRoot.appendChild(style);
  }
});

onUnmounted(() => {
  if (pickerRef.value) {
    pickerRef.value.removeEventListener('emoji-click', emojiClickListener);
  }
});
</script>

<template>
  <div
    ref="overlayCard"
    class="z-50"
    :style="{ ...floatingStyles, position: 'fixed' }"
  >
    <GrammarOverlayCard>
      <div style="height: 400px; width: 344px">
        <emoji-picker
          ref="pickerRef"
          :class="darkModeStore.isDark ? 'dark' : 'light'"
        />
      </div>
    </GrammarOverlayCard>
  </div>
</template>

<style scoped>
emoji-picker {
  height: 100%;
  width: 100%;
  border-radius: 20px;
  --num-columns: 8;
  --input-border-radius: 0.25rem;
  --input-font-size: 0.875rem;
}

emoji-picker.light {
  --background: var(--light-bg-primary);
  --border-color: var(--light-separator-transparency);
  --input-font-color: var(--light-label-secondary);
  --input-placeholder-color: var(--light-label-secondary);
  --input-border-color: var(--light-separator-no-transparency);
  --button-hover-background: var(--light-bg-quaternary);
  --indicator-color: var(--light-label-secondary);
}

emoji-picker.dark {
  --background: var(--dark-bg-primary);
  --border-color: var(--dark-separator-no-transparency);
  --input-font-color: var(--dark-label-secondary);
  --input-placeholder-color: var(--dark-label-secondary);
  --input-border-color: var(--dark-separator-no-transparency);
  --button-hover-background: var(--dark-bg-quaternary);
  --indicator-color: var(--dark-label-secondary);
}
</style>
